import React from 'react';
import { useTeamManagement } from '../hooks/useTeamManagement';
import './TeamSaveStatus.css';

const TeamSaveStatus: React.FC = () => {
  const { 
    saveStatus, 
    isSaving, 
    hasUnsavedChanges, 
    lastSaved, 
    isAuthenticated, 
    saveTeam,
    backupTeam 
  } = useTeamManagement();

  // Format last saved time
  const formatLastSaved = (date: Date | null): string => {
    if (!date) return '';
    
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get status info
  const getStatusInfo = () => {
    if (!isAuthenticated) {
      return {
        icon: '🔒',
        text: 'Sign in to save progress',
        className: 'status-offline'
      };
    }
    
    if (isSaving) {
      return {
        icon: '💾',
        text: 'Saving to cloud...',
        className: 'status-saving'
      };
    }
    
    if (hasUnsavedChanges) {
      return {
        icon: '⚠️',
        text: 'You have unsaved changes',
        className: 'status-unsaved'
      };
    }
    
    if (lastSaved) {
      return {
        icon: '✅',
        text: `Last saved ${formatLastSaved(lastSaved)}`,
        className: 'status-saved'
      };
    }
    
    return {
      icon: '📝',
      text: 'Ready to save your team',
      className: 'status-ready'
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="team-save-status">
      <div className={`save-status-indicator ${statusInfo.className}`}>
        <span className="status-icon">{statusInfo.icon}</span>
        <span className="status-text">{statusInfo.text}</span>
      </div>
      
      {isAuthenticated && (
        <div className="save-actions">
          <button
            className="save-now-btn"
            onClick={() => saveTeam()}
            disabled={isSaving}
            title={hasUnsavedChanges ? "Save unsaved changes to cloud" : "Save current team to cloud"}
          >
            {isSaving ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'Save Team'}
          </button>
          
          <button
            className="backup-btn"
            onClick={backupTeam}
            disabled={isSaving}
            title="Download team as JSON backup file"
          >
            💾 Backup
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamSaveStatus; 